export const matchesSchemaConst = (value, schemaConst) => {
  if (schemaConst === undefined) return false;
  if (value === schemaConst) return true;

  if (typeof schemaConst === "boolean") {
    return value === String(schemaConst) || value === (schemaConst ? "Yes" : "No");
  }

  return false;
};

export const isEnabledSelection = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  return ["true", "yes", "enabled", "enable", "on"].includes(normalizedValue);
};

const deleteKeys = (target, keys = []) => {
  keys.forEach((key) => {
    delete target[key];
  });
};

const removeDraftKeys = (value) => {
  if (Array.isArray(value)) {
    return value.map(removeDraftKeys);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((acc, [key, nestedValue]) => {
    if (String(key).startsWith("__")) return acc;
    acc[key] = removeDraftKeys(nestedValue);
    return acc;
  }, {});
};

const CLOUD_SOURCE_KEYS = [
  "Bucket Type",
  "Bucket Name",
  "Folder Path",
  "Access Key",
  "Secret Key",
];

const LOCAL_TRANSFER_KEYS = ["Network Path"];

const DEVICE_SOURCE_KEYS = ["Registered Devices", "Select Stream Id"];

const PLATFORM_SOURCE_KEYS = ["Select Project", "Select Dataset"];

const DATASET_CONTENT_KEYS = [
  "Directory Format",
  "Import/Load Labels",
  "Map to New Labels",
  "Annotation Format",
];

const IMAGE_CONTENT_KEYS = ["Image Extensions"];

const VIDEO_CONTENT_KEYS = [
  "Video Extensions",
  "Extraction Frame Rate",
  "Video for Inference",
];

const AI_RELATED_KEYS = [
  "Task Name",
  "AI Assist",
  "Model Details",
  "Experiment Details",
  "Foundational Models",
  "Foundational Model Details",
  "Score Threshold",
  "Label Map",
  "LABELS_PROMPT_KEY",
  "Prompt Labels",
  "Images Per Task",
  "Images Pertask",
  "Tasks Per Taskset",
  "Taskset Group Name",
  "Task Group Name",
  "Task group name",
];

const isObjectWithKeys = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const sanitizeDatasetWizardData = (dataObj = {}) => {
  if (!isObjectWithKeys(dataObj)) return dataObj;

  const next = removeDraftKeys(dataObj);
  const selectedLabelsMode = next["Add Labels"];
  const selectedSource = next["Select Data Source"] || next.Source;
  const selectedContent = next.Content;
  const aiAnnotateEnabled =
    isEnabledSelection(next["Use AI to Annotate"]) ||
    isEnabledSelection(next["Use AI Assist"]);

  if (selectedLabelsMode === "Comma-separated values") {
    deleteKeys(next, ["detailed_labels"]);
  } else if (selectedLabelsMode === "Detailed array") {
    deleteKeys(next, ["Object Details", "Labels", "labels"]);
  } else if (selectedLabelsMode === "Skip for now") {
    deleteKeys(next, ["Object Details", "Labels", "labels", "detailed_labels"]);
  }

  if (selectedSource) {
    if (selectedSource !== "CloudS3") deleteKeys(next, CLOUD_SOURCE_KEYS);
    if (selectedSource !== "LocalTransfer") deleteKeys(next, LOCAL_TRANSFER_KEYS);
    if (selectedSource !== "Registered_Device") deleteKeys(next, DEVICE_SOURCE_KEYS);
    if (selectedSource !== "PlatformImport") deleteKeys(next, PLATFORM_SOURCE_KEYS);
  }

  if (selectedContent) {
    if (selectedContent !== "Dataset") deleteKeys(next, DATASET_CONTENT_KEYS);
    if (selectedContent !== "ImagesOnly") deleteKeys(next, IMAGE_CONTENT_KEYS);
    if (selectedContent !== "VideosOnly") deleteKeys(next, VIDEO_CONTENT_KEYS);
  }

  if (!isEnabledSelection(next["Avoid Similar Images"])) {
    deleteKeys(next, ["Similarity Threshold"]);
  }

  if (!aiAnnotateEnabled || (selectedContent && selectedContent !== "ImagesOnly")) {
    deleteKeys(next, AI_RELATED_KEYS);
  } else {
    const aiAssistSelection = String(next["AI Assist"] || "").toLowerCase();

    if (aiAssistSelection.includes("foundation")) {
      deleteKeys(next, ["Model Details", "Experiment Details"]);
    } else if (aiAssistSelection.includes("experiment")) {
      deleteKeys(next, [
        "Model Details",
        "Foundational Models",
        "Foundational Model Details",
      ]);
    } else if (aiAssistSelection.includes("model")) {
      deleteKeys(next, [
        "Experiment Details",
        "Foundational Models",
        "Foundational Model Details",
      ]);
    }
  }

  return next;
};

export const getActiveSchemaProperty = (schemaObj, fieldName, dataObj = {}) => {
  const visited = new Set();

  const walk = (node) => {
    if (!node || typeof node !== "object" || visited.has(node)) return null;
    visited.add(node);

    if (node.properties?.[fieldName]) {
      return node.properties[fieldName];
    }

    for (const childSchema of Object.values(node.properties || {})) {
      const found = walk(childSchema);
      if (found) return found;
    }

    for (const [dependencyKey, dependency] of Object.entries(node.dependencies || {})) {
      if (Array.isArray(dependency?.oneOf)) {
        const dependencyValue = dataObj?.[dependencyKey];
        const selectedBranch = dependency.oneOf.find((branch) =>
          matchesSchemaConst(
            dependencyValue,
            branch?.properties?.[dependencyKey]?.const
          )
        );

        if (!selectedBranch) continue;

        const found = walk(selectedBranch);
        if (found) return found;
        continue;
      }

      const found = walk(dependency);
      if (found) return found;
    }

    if (node.items) {
      return walk(node.items);
    }

    return null;
  };

  return walk(schemaObj);
};

const normalizeLabelsForSchema = (dataObj = {}, schemaObj) => {
  const labelsSchema = getActiveSchemaProperty(schemaObj, "Labels", dataObj);
  const expectsNamedLabelObjects =
    labelsSchema?.type === "array" &&
    labelsSchema?.items?.type === "object" &&
    labelsSchema?.items?.properties?.name;

  if (!expectsNamedLabelObjects) return dataObj;

  const currentLabels = dataObj?.Labels;
  const labelsSource = Array.isArray(currentLabels)
    ? currentLabels
    : labelsSchema.default;

  if (!Array.isArray(labelsSource)) return dataObj;

  return {
    ...dataObj,
    Labels: labelsSource.map((label) => {
      if (typeof label === "string") {
        return { name: label, prompt: "" };
      }

      if (label && typeof label === "object") {
        return {
          ...label,
          name: label.name == null ? "" : String(label.name),
          prompt: label.prompt == null ? "" : String(label.prompt),
        };
      }

      return { name: "", prompt: "" };
    }),
  };
};

const normalizeEnumValue = (fieldSchema, value) => {
  if (!fieldSchema?.enum || value === undefined || value === null) return value;
  if (fieldSchema.enum.includes(value)) return value;

  const enumNameIndex = fieldSchema.enumNames?.findIndex(
    (enumName) => enumName === value
  );

  if (enumNameIndex >= 0) {
    return fieldSchema.enum[enumNameIndex];
  }

  return value;
};

const normalizeEnumValuesForSchema = (dataObj = {}, schemaObj) => {
  const visit = (schemaNode, dataNode) => {
    if (!schemaNode || dataNode === undefined || dataNode === null) {
      return dataNode;
    }

    if (schemaNode.enum) {
      return normalizeEnumValue(schemaNode, dataNode);
    }

    if (Array.isArray(dataNode)) {
      if (!schemaNode.items) return dataNode;
      return dataNode.map((item) => visit(schemaNode.items, item));
    }

    if (typeof dataNode !== "object") {
      return dataNode;
    }

    let nextData = { ...dataNode };

    Object.entries(schemaNode.properties || {}).forEach(([fieldKey, fieldSchema]) => {
      if (nextData[fieldKey] === undefined) return;
      nextData[fieldKey] = visit(fieldSchema, nextData[fieldKey]);
    });

    Object.entries(schemaNode.dependencies || {}).forEach(([dependencyKey, dependency]) => {
      if (Array.isArray(dependency?.oneOf)) {
        const dependencyValue = nextData[dependencyKey];
        const selectedBranch = dependency.oneOf.find((branch) =>
          matchesSchemaConst(
            dependencyValue,
            branch?.properties?.[dependencyKey]?.const
          )
        );

        if (selectedBranch) {
          nextData = visit(selectedBranch, nextData);
        }

        return;
      }

      nextData = visit(dependency, nextData);
    });

    return nextData;
  };

  return visit(schemaObj, dataObj);
};

export const normalizeSchemaFormData = (dataObj = {}, schemaObj) => {
  const sanitizedData = sanitizeDatasetWizardData(dataObj);
  const withLabels = normalizeLabelsForSchema(sanitizedData, schemaObj);
  const withEnumValues = normalizeEnumValuesForSchema(withLabels, schemaObj);

  return withEnumValues;
};

export const mergeReviewDataWithEditableData = (
  summaryData = {},
  editableData = {},
  schemaObj
) => normalizeSchemaFormData(
  sanitizeDatasetWizardData({
    ...summaryData,
    ...editableData,
  }),
  schemaObj
);
